import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    FiBox,
    FiChevronDown,
    FiChevronUp,
    FiHome,
    FiPercent,
    FiPrinter,
    FiSettings,
    FiUsers,
} from 'react-icons/fi';
import { TiPin } from 'react-icons/ti';
import { RiStore2Line } from 'react-icons/ri';
import {
    BARCODE_PREFIX_MAX_LENGTH,
    BARCODE_MODE_OPTIONS,
    DEFAULT_BARCODE_PREFIX_MAPPINGS,
    getBarcodePrefixMappings,
    saveBarcodePrefixMappings,
    sanitizeBarcodePrefixInput,
} from '../utils/barcodeSettings';
import {
    DEFAULT_PRINT_MODE,
    PRINT_MODE_DESCRIPTIONS,
    PRINT_MODE_LABELS,
    PRINT_MODE_OPTIONS,
    getPrintMode,
    savePrintMode,
} from '../utils/printSettings';
import {
    createBranch,
    createCashRegister,
    deleteBranch,
    getBarcodePricingRule,
    getBranchRequestConfig,
    updateBarcodePricingRule,
    updateBranchRequestConfig,
    getBranches,
    getUsers,
    updateBranch,
    getTaxes,
    createTax,
    updateTax,
    getBranchTaxes,
    updateBranchTaxes,
} from '../api';
import BranchUsersPage from './BranchUsersPage';
import CashRegistersPage from './CashRegistersPage';
import ProductList from '../components/ProductList';
import AdminPriceListPage from './AdminPriceListPage';
import CategoriesAdminPage from './CategoriesAdminPage';
import useCurrentUser from '../hooks/useCurrentUser';
import useConfirmDialog from '../hooks/useConfirmDialog';
import { hasAnyPermission, hasPermission } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';

const MODE_LABELS = {
    weight: 'Peso',
    amount: 'Cantidad',
    price: 'Precio',
};

const CLIENT_TYPE_LABELS = {
    detalle: 'Detalle',
    mayorista: 'Mayorista',
    importador: 'Importador',
};

const PRICE_SYNC_TYPE_LABELS = {
    percent: 'Porcentaje',
    fixed: 'Monto fijo',
};

const buildRegisterTemplate = (index) => ({
    name: `Caja ${index + 1}`,
    isActive: true,
    allowedClientTypes: [],
    discountApproverUserId: '',
    maxOpenSales: '5',
    users: [],
});

const SETTINGS_TOPICS = [
    {
        id: 'branches',
        label: 'Sucursales',
        description: 'Configuracion y estructura.',
        sections: [
            {
                id: 'branch-directory',
                label: 'Listado de sucursales',
                description: 'Crear, editar y eliminar.',
                branchScoped: false,
            },
            {
                id: 'branch-price-sync',
                label: 'Sincronizacion de precios',
                description: 'Casa matriz y ajustes.',
                branchScoped: true,
            },
        ],
    },
    {
        id: 'catalog',
        label: 'Catalogo',
        description: 'Productos y categorias.',
        sections: [
            {
                id: 'catalog-categories',
                label: 'Categorias',
                description: 'Gestion de categorias.',
                branchScoped: true,
            },
            {
                id: 'catalog-price-list',
                label: 'Lista de precios',
                description: 'Editar precios y disponibilidad.',
                branchScoped: true,
            },
            {
                id: 'catalog-products',
                label: 'Productos',
                description: 'Lista y mantenimiento.',
                branchScoped: true,
            },
        ],
    },
    {
        id: 'taxes',
        label: 'Impuestos',
        description: 'IVA y configuracion fiscal.',
        sections: [
            {
                id: 'taxes-manage',
                label: 'Impuestos',
                description: 'Configura impuestos y activacion por sucursal.',
                branchScoped: false,
            },
        ],
    },
    {
        id: 'users',
        label: 'Usuarios',
        description: 'Acceso general y roles.',
        sections: [
            {
                id: 'users-access',
                label: 'Usuarios',
                description: 'Gestiona accesos y roles.',
                branchScoped: false,
            },
        ],
    },
    {
        id: 'registers',
        label: 'Cajas',
        description: 'Cajas, usuarios y codigos de barras.',
        sections: [
            {
                id: 'registers-cash',
                label: 'Administrar cajas',
                description: 'Crear y asignar usuarios.',
                branchScoped: true,
            },
            {
                id: 'registers-barcode',
                label: 'Codigos de barras',
                description: 'Prefijos y modo de lectura.',
                branchScoped: false,
            },
            {
                id: 'registers-printing',
                label: 'Impresion',
                description: 'Preferencias de impresion.',
                branchScoped: false,
            },
        ],
    },
];

const SettingsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { branchId: routeBranchId } = useParams();
    const [activeBranchId, setActiveBranchId] = useState(
        routeBranchId || localStorage.getItem('selectedBranchId') || ''
    );
    const [activeTopic, setActiveTopic] = useState('branches');
    const [activeSection, setActiveSection] = useState('branch-directory');
    const [mappings, setMappings] = useState([]);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [barcodePricingRule, setBarcodePricingRule] = useState({
        mode: 'quantity',
        thresholds: {
            detalle: '',
            mayorista: '',
            importador: '',
        },
    });
    const [barcodeRuleLoading, setBarcodeRuleLoading] = useState(false);
    const [barcodeRuleError, setBarcodeRuleError] = useState('');
    const [barcodeRuleSaved, setBarcodeRuleSaved] = useState(false);
    const [branches, setBranches] = useState([]);
    const [branchLoading, setBranchLoading] = useState(false);
    const [branchError, setBranchError] = useState('');
    const [branchPage, setBranchPage] = useState(1);
    const [editingBranchId, setEditingBranchId] = useState(null);
    const [editedBranchName, setEditedBranchName] = useState('');
    const [newBranchName, setNewBranchName] = useState('');
    const [branchCreateOpen, setBranchCreateOpen] = useState(false);
    const [branchCreating, setBranchCreating] = useState(false);
    const [branchCreateError, setBranchCreateError] = useState('');
    const [branchCreateNotice, setBranchCreateNotice] = useState('');
    const [newBranchRegisters, setNewBranchRegisters] = useState(() => [buildRegisterTemplate(0)]);
    const [branchCreateUsers, setBranchCreateUsers] = useState([]);
    const [branchCreateUsersLoading, setBranchCreateUsersLoading] = useState(false);
    const [branchCreateUsersError, setBranchCreateUsersError] = useState('');
    const [priceSync, setPriceSync] = useState({
        sourceBranchId: '',
        adjustmentType: '',
        adjustmentValue: '',
    });
    const [branchRequestConfig, setBranchRequestConfig] = useState({
        mainBranchId: '',
        resolvedMainBranchId: null,
    });
    const [branchRequestConfigLoading, setBranchRequestConfigLoading] = useState(false);
    const [branchRequestConfigError, setBranchRequestConfigError] = useState('');
    const [branchRequestConfigSaved, setBranchRequestConfigSaved] = useState(false);
    const [priceSyncError, setPriceSyncError] = useState('');
    const [priceSyncSaved, setPriceSyncSaved] = useState(false);
    const [priceSyncLoading, setPriceSyncLoading] = useState(false);
    const [printMode, setPrintMode] = useState(DEFAULT_PRINT_MODE);
    const [printModeSaved, setPrintModeSaved] = useState(false);
    const [taxes, setTaxes] = useState([]);
    const [taxesLoading, setTaxesLoading] = useState(false);
    const [taxesError, setTaxesError] = useState('');
    const [taxForm, setTaxForm] = useState({
        name: '',
        rate: '',
        applyToAllProducts: true,
        isActive: true,
    });
    const [taxSaving, setTaxSaving] = useState(false);
    const [taxEdits, setTaxEdits] = useState({});
    const [branchTaxMap, setBranchTaxMap] = useState({});
    const [branchTaxLoading, setBranchTaxLoading] = useState(false);
    const [branchTaxError, setBranchTaxError] = useState('');
    const [branchTaxSaving, setBranchTaxSaving] = useState({});
    const [branchTaxDirty, setBranchTaxDirty] = useState({});
    const [newBranchTaxSelection, setNewBranchTaxSelection] = useState({});
    const { user } = useCurrentUser();
    const { confirm } = useConfirmDialog();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const canReadBranches = hasPermission(user, 'branch.read');
    const canUpdateBranch = hasPermission(user, 'branch.update');
    const canCreateBranch = hasPermission(user, 'branch.create');
    const canDeleteBranch = hasPermission(user, 'branch.delete');
    const canReadTaxes = hasPermission(user, 'tax.read');
    const canManageTaxes = hasPermission(user, 'tax.manage');
    const canReadUsers = hasPermission(user, 'user.read');
    const canReadProducts = hasPermission(user, 'product.read');
    const canCreateProduct = hasPermission(user, 'product.create');
    const canUpdateProduct = hasPermission(user, 'product.update');
    const canDeleteProduct = hasPermission(user, 'product.delete');
    const canReadCategories = hasAnyPermission(user, ['category.read', 'category.manage']);
    const canAccessRegisters = hasAnyPermission(user, [
        'cash_register.read',
        'cash_register.manage',
        'cash_register.assign_users',
    ]);
    const canManageRegisters = hasPermission(user, 'cash_register.manage');

    const branchCreateAvailableUsers = useMemo(() => {
        if (!canReadUsers) return [];
        return (branchCreateUsers || [])
            .filter((entry) => hasPermission(entry, 'branch.access.any'))
            .sort((a, b) => (a.name || a.username || '').localeCompare(b.name || b.username || ''));
    }, [branchCreateUsers, canReadUsers]);

    const activeTopicConfig = useMemo(
        () => SETTINGS_TOPICS.find((topic) => topic.id === activeTopic) || SETTINGS_TOPICS[0],
        [activeTopic]
    );

    const activeSectionConfig = useMemo(
        () =>
            activeTopicConfig.sections.find((section) => section.id === activeSection)
            || activeTopicConfig.sections[0],
        [activeSection, activeTopicConfig]
    );

    const topicIcons = useMemo(
        () => ({
            branches: <RiStore2Line className="text-base" />,
            catalog: <FiBox className="text-base" />,
            taxes: <FiPercent className="text-base" />,
            users: <FiUsers className="text-base" />,
            registers: <FiSettings className="text-base" />,
        }),
        []
    );

    const sectionIcons = useMemo(
        () => ({
            'branch-directory': <RiStore2Line className="text-sm" />,
            'branch-price-sync': <FiSettings className="text-sm" />,
            'catalog-categories': <FiBox className="text-sm" />,
            'catalog-price-list': <FiBox className="text-sm" />,
            'catalog-products': <FiBox className="text-sm" />,
            'taxes-manage': <FiPercent className="text-sm" />,
            'users-access': <FiUsers className="text-sm" />,
            'registers-cash': <FiHome className="text-sm" />,
            'registers-barcode': <FiSettings className="text-sm" />,
            'registers-printing': <FiPrinter className="text-sm" />,
        }),
        []
    );

    const isBranchScopedSection = Boolean(activeSectionConfig?.branchScoped);

    const activeBranch = useMemo(
        () => branches.find((branch) => String(branch.id) === String(activeBranchId)),
        [branches, activeBranchId]
    );

    const branchLabel = activeBranch?.branchName || 'Sucursal seleccionada';
    const branchPageSize = 8;
    const branchTotalPages = Math.max(1, Math.ceil(branches.length / branchPageSize));
    const currentBranchPage = Math.min(branchPage, branchTotalPages);
    const branchStartIndex = (currentBranchPage - 1) * branchPageSize;
    const branchEndIndex = Math.min(branchStartIndex + branchPageSize, branches.length);
    const pagedBranches = branches.slice(branchStartIndex, branchEndIndex);

    useEffect(() => {
        setMappings(getBarcodePrefixMappings());
    }, []);

    useEffect(() => {
        let mounted = true;
        const loadBarcodeRule = async () => {
            setBarcodeRuleLoading(true);
            setBarcodeRuleError('');
            try {
                const data = await getBarcodePricingRule();
                if (!mounted) return;
                const thresholds = data?.thresholds || {};
                setBarcodePricingRule({
                    mode: data?.mode === 'subtotal' ? 'subtotal' : 'quantity',
                    thresholds: {
                        detalle: thresholds.detalle ?? '',
                        mayorista: thresholds.mayorista ?? '',
                        importador: thresholds.importador ?? '',
                    },
                });
                setBarcodeRuleSaved(false);
            } catch (err) {
                if (!mounted) return;
                setBarcodeRuleError(err?.response?.data?.message || 'No se pudo cargar la regla global.');
            } finally {
                if (mounted) setBarcodeRuleLoading(false);
            }
        };
        loadBarcodeRule();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        let mounted = true;
        const loadBranchRequestConfig = async () => {
            setBranchRequestConfigLoading(true);
            setBranchRequestConfigError('');
            try {
                const config = await getBranchRequestConfig();
                if (!mounted) return;
                setBranchRequestConfig({
                    mainBranchId: config?.mainBranchId ? String(config.mainBranchId) : '',
                    resolvedMainBranchId: config?.resolvedMainBranchId ?? null,
                });
                setBranchRequestConfigSaved(false);
            } catch (err) {
                if (!mounted) return;
                setBranchRequestConfigError(
                    err?.response?.data?.message || 'No se pudo cargar la configuracion de solicitudes.'
                );
            } finally {
                if (mounted) setBranchRequestConfigLoading(false);
            }
        };
        loadBranchRequestConfig();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        setPrintMode(getPrintMode());
    }, []);

    useEffect(() => {
        fetchTaxes();
    }, [canReadTaxes]);

    useEffect(() => {
        if (activeSectionConfig?.id !== 'taxes-manage') return;
        if (!branches.length) return;
        fetchBranchTaxes();
    }, [activeSectionConfig?.id, branches, taxes, canReadTaxes]);

    useEffect(() => {
        if (!taxes.length) {
            setTaxEdits({});
            return;
        }
        const nextEdits = {};
        taxes.forEach((tax) => {
            nextEdits[tax.id] = {
                name: tax.name || '',
                rate: tax.rate ?? '',
                applyToAllProducts: !!tax.applyToAllProducts,
                isActive: tax.isActive !== false,
            };
        });
        setTaxEdits(nextEdits);
    }, [taxes]);

    useEffect(() => {
        if (!branchCreateOpen) return;
        const defaults = {};
        taxes.forEach((tax) => {
            defaults[tax.id] = tax.isActive !== false;
        });
        setNewBranchTaxSelection(defaults);
    }, [branchCreateOpen, taxes]);

    useEffect(() => {
        setActiveBranchId(routeBranchId || localStorage.getItem('selectedBranchId') || '');
    }, [routeBranchId]);

    useEffect(() => {
        if (!branchCreateOpen) return;
        if (!canReadUsers) {
            setBranchCreateUsers([]);
            setBranchCreateUsersError('');
            setBranchCreateUsersLoading(false);
            return;
        }
        let isMounted = true;
        const fetchUsers = async () => {
            setBranchCreateUsersLoading(true);
            setBranchCreateUsersError('');
            try {
                const data = await getUsers({ pageSize: 0 });
                if (!isMounted) return;
                setBranchCreateUsers(normalizePaginatedResponse(data).data || []);
            } catch (err) {
                if (!isMounted) return;
                setBranchCreateUsers([]);
                setBranchCreateUsersError(err.message || 'No se pudieron cargar usuarios.');
            } finally {
                if (isMounted) setBranchCreateUsersLoading(false);
            }
        };
        fetchUsers();
        return () => {
            isMounted = false;
        };
    }, [branchCreateOpen, canReadUsers]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const topicParam = params.get('topic');
        const sectionParam = params.get('section');
        const nextTopic = SETTINGS_TOPICS.find((topic) => topic.id === topicParam)
            ? topicParam
            : activeTopic;
        const topicConfig = SETTINGS_TOPICS.find((topic) => topic.id === nextTopic) || SETTINGS_TOPICS[0];
        const nextSection = topicConfig.sections.find((section) => section.id === sectionParam)
            ? sectionParam
            : topicConfig.sections[0].id;

        if (nextTopic !== activeTopic) {
            setActiveTopic(nextTopic);
        }
        if (nextSection !== activeSection) {
            setActiveSection(nextSection);
        }
    }, [location.search, activeTopic, activeSection]);

    useEffect(() => {
        if (activeTopicConfig.sections.some((section) => section.id === activeSection)) return;
        setActiveSection(activeTopicConfig.sections[0].id);
    }, [activeSection, activeTopicConfig]);

    const updateQueryParams = (nextTopic, nextSection) => {
        const params = new URLSearchParams(location.search);
        params.set('topic', nextTopic);
        params.set('section', nextSection);
        navigate({ pathname: '/settings', search: params.toString() }, { replace: true });
    };

    const fetchBranches = async () => {
        if (!canReadBranches) {
            setBranches([]);
            setBranchError('No tienes permisos para ver sucursales.');
            return;
        }
        setBranchLoading(true);
        setBranchError('');
        try {
            const data = await getBranches({ pageSize: 0 });
            const normalized = normalizePaginatedResponse(data);
            const list = normalized.data || [];
            setBranches(list);
            setPriceSyncError('');
            setPriceSyncSaved(false);
        } catch (err) {
            if (err.response?.status === 403) {
                setBranchError('No tienes permisos para ver sucursales.');
                setPriceSyncError('No tienes permisos para ver la configuracion de precios.');
            } else {
                setBranchError('No se pudieron cargar las sucursales.');
                setPriceSyncError('No se pudo cargar la configuracion de precios.');
            }
        } finally {
            setBranchLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, [canReadBranches]);

    useEffect(() => {
        if (branchPage > branchTotalPages) {
            setBranchPage(branchTotalPages);
        }
    }, [branchPage, branchTotalPages]);

    useEffect(() => {
        if (!branches.length) {
            if (activeBranchId) {
                setActiveBranchId('');
                localStorage.removeItem('selectedBranchId');
            }
            return;
        }
        const hasBranch = branches.some((branch) => String(branch.id) === String(activeBranchId));
        if (hasBranch) return;
        const nextId = String(branches[0].id);
        setActiveBranchId(nextId);
        localStorage.setItem('selectedBranchId', nextId);
    }, [branches, activeBranchId]);

    useEffect(() => {
        if (!activeBranchId || !branches.length) return;
        const current = branches.find((branch) => String(branch.id) === String(activeBranchId));
        if (!current) return;
        setPriceSync({
            sourceBranchId: current.priceSyncSourceBranchId
                ? String(current.priceSyncSourceBranchId)
                : '',
            adjustmentType: current.priceSyncAdjustmentType || '',
            adjustmentValue:
                current.priceSyncAdjustmentValue !== null
                && current.priceSyncAdjustmentValue !== undefined
                    ? String(current.priceSyncAdjustmentValue)
                    : '',
        });
        setPriceSyncSaved(false);
    }, [activeBranchId, branches]);

    const sanitizeAdjustmentInput = (value) => value.replace(/[^\d-]/g, '');

    const handlePriceSyncSourceChange = (value) => {
        setPriceSync((prev) => ({
            sourceBranchId: value,
            adjustmentType: value ? (prev.adjustmentType || 'percent') : '',
            adjustmentValue: value ? prev.adjustmentValue : '',
        }));
        setPriceSyncSaved(false);
        setPriceSyncError('');
    };

    const handlePriceSyncTypeChange = (value) => {
        setPriceSync((prev) => ({
            ...prev,
            adjustmentType: value,
        }));
        setPriceSyncSaved(false);
    };

    const handlePriceSyncValueChange = (value) => {
        setPriceSync((prev) => ({
            ...prev,
            adjustmentValue: sanitizeAdjustmentInput(value),
        }));
        setPriceSyncSaved(false);
    };

    const parseAdjustmentValue = (value) => {
        if (value === '' || value === null || value === undefined) return 0;
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) return null;
        return Math.round(parsed);
    };

    const handleSavePriceSync = async () => {
        if (!activeBranchId) {
            setPriceSyncError('Sucursal no definida.');
            return;
        }
        if (!canUpdateBranch) {
            setPriceSyncError('No tienes permisos para actualizar esta configuracion.');
            return;
        }
        setPriceSyncLoading(true);
        try {
            const payload = {};
            if (!priceSync.sourceBranchId) {
                payload.priceSyncSourceBranchId = null;
                payload.priceSyncAdjustmentType = null;
                payload.priceSyncAdjustmentValue = null;
            } else {
                if (!PRICE_SYNC_TYPE_LABELS[priceSync.adjustmentType]) {
                    setPriceSyncError('Selecciona un tipo de ajuste valido.');
                    setPriceSyncLoading(false);
                    return;
                }
                const parsedValue = parseAdjustmentValue(priceSync.adjustmentValue);
                if (parsedValue === null) {
                    setPriceSyncError('El ajuste debe ser un numero valido.');
                    setPriceSyncLoading(false);
                    return;
                }
                payload.priceSyncSourceBranchId = Number(priceSync.sourceBranchId);
                payload.priceSyncAdjustmentType = priceSync.adjustmentType;
                payload.priceSyncAdjustmentValue = parsedValue;
            }
            await updateBranch(activeBranchId, payload);
            setBranches((prev) =>
                prev.map((branch) =>
                    String(branch.id) === String(activeBranchId)
                        ? {
                            ...branch,
                            priceSyncSourceBranchId: payload.priceSyncSourceBranchId,
                            priceSyncAdjustmentType: payload.priceSyncAdjustmentType,
                            priceSyncAdjustmentValue: payload.priceSyncAdjustmentValue,
                        }
                        : branch
                )
            );
            setPriceSyncError('');
            setPriceSyncSaved(true);
        } catch (err) {
            setPriceSyncSaved(false);
            if (err.response?.status === 403) {
                setPriceSyncError('No tienes permisos para actualizar esta configuracion.');
            } else {
                setPriceSyncError(
                    err.response?.data?.message || 'No se pudo guardar la configuracion de precios.'
                );
            }
        } finally {
            setPriceSyncLoading(false);
        }
    };

    const handleBranchRequestMainChange = (value) => {
        setBranchRequestConfig((prev) => ({
            ...prev,
            mainBranchId: value,
        }));
        setBranchRequestConfigSaved(false);
        setBranchRequestConfigError('');
    };

    const handleSaveBranchRequestConfig = async () => {
        setBranchRequestConfigLoading(true);
        setBranchRequestConfigError('');
        try {
            const payload = {
                mainBranchId: branchRequestConfig.mainBranchId
                    ? Number(branchRequestConfig.mainBranchId)
                    : null,
            };
            const saved = await updateBranchRequestConfig(payload);
            setBranchRequestConfig({
                mainBranchId: saved?.mainBranchId ? String(saved.mainBranchId) : '',
                resolvedMainBranchId: saved?.resolvedMainBranchId ?? null,
            });
            setBranchRequestConfigSaved(true);
        } catch (err) {
            setBranchRequestConfigSaved(false);
            setBranchRequestConfigError(
                err?.response?.data?.message || 'No se pudo guardar la configuracion de solicitudes.'
            );
        } finally {
            setBranchRequestConfigLoading(false);
        }
    };

    const handlePrefixChange = (index, value) => {
        setMappings((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                prefix: sanitizeBarcodePrefixInput(value),
            };
            return next;
        });
        setSaved(false);
    };

    const handleModeChange = (index, value) => {
        setMappings((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                mode: value,
            };
            return next;
        });
        setSaved(false);
    };

    const handleAddMapping = () => {
        setMappings((prev) => [...prev, { prefix: '', mode: 'weight' }]);
        setSaved(false);
    };

    const handleRemoveMapping = (index) => {
        setMappings((prev) => prev.filter((_, i) => i !== index));
        setSaved(false);
    };

    const validateMappings = (nextMappings) => {
        const normalized = nextMappings.map((item) => ({
            prefix: sanitizeBarcodePrefixInput(item?.prefix),
            mode: BARCODE_MODE_OPTIONS.includes(item?.mode) ? item.mode : 'weight',
        }));

        const prefixes = normalized.map((item) => item.prefix).filter(Boolean);
        if (prefixes.length === 0) {
            return { valid: false, message: 'Agrega al menos un prefijo.' };
        }

        const uniquePrefixes = new Set(prefixes);
        if (uniquePrefixes.size !== prefixes.length) {
            return { valid: false, message: 'Los prefijos deben ser unicos.' };
        }

        if (normalized.some((item) => !item.prefix)) {
            return { valid: false, message: 'Completa todos los prefijos.' };
        }

        return { valid: true, normalized };
    };

    const handleSave = () => {
        const result = validateMappings(mappings);
        if (!result.valid) {
            setError(result.message);
            setSaved(false);
            return;
        }
        const savedMappings = saveBarcodePrefixMappings(result.normalized);
        setMappings(savedMappings);
        setError('');
        setSaved(true);
    };

    const handleReset = () => {
        const savedMappings = saveBarcodePrefixMappings(DEFAULT_BARCODE_PREFIX_MAPPINGS);
        setMappings(savedMappings);
        setError('');
        setSaved(false);
    };

    const handleBarcodeRuleModeChange = (mode) => {
        setBarcodePricingRule((prev) => ({
            ...prev,
            mode: mode === 'subtotal' ? 'subtotal' : 'quantity',
        }));
        setBarcodeRuleSaved(false);
    };

    const handleBarcodeRuleThresholdChange = (key, value) => {
        const sanitized = String(value ?? '').replace(/[^\d]/g, '');
        setBarcodePricingRule((prev) => ({
            ...prev,
            thresholds: {
                ...prev.thresholds,
                [key]: sanitized,
            },
        }));
        setBarcodeRuleSaved(false);
    };

    const handleSaveBarcodeRule = async () => {
        const payload = {
            mode: barcodePricingRule.mode === 'subtotal' ? 'subtotal' : 'quantity',
            thresholds: {
                detalle: barcodePricingRule.thresholds.detalle === '' ? null : Number(barcodePricingRule.thresholds.detalle),
                mayorista: barcodePricingRule.thresholds.mayorista === '' ? null : Number(barcodePricingRule.thresholds.mayorista),
                importador: barcodePricingRule.thresholds.importador === '' ? null : Number(barcodePricingRule.thresholds.importador),
            },
        };
        setBarcodeRuleLoading(true);
        setBarcodeRuleError('');
        try {
            const response = await updateBarcodePricingRule(payload);
            const savedRule = response?.rule || payload;
            setBarcodePricingRule({
                mode: savedRule.mode === 'subtotal' ? 'subtotal' : 'quantity',
                thresholds: {
                    detalle: savedRule?.thresholds?.detalle ?? '',
                    mayorista: savedRule?.thresholds?.mayorista ?? '',
                    importador: savedRule?.thresholds?.importador ?? '',
                },
            });
            setBarcodeRuleSaved(true);
        } catch (err) {
            setBarcodeRuleError(err?.response?.data?.message || 'No se pudo guardar la regla global.');
            setBarcodeRuleSaved(false);
        } finally {
            setBarcodeRuleLoading(false);
        }
    };

    const handlePrintModeChange = (value) => {
        setPrintMode(value);
        setPrintModeSaved(false);
    };

    const handleSavePrintMode = () => {
        const savedMode = savePrintMode(printMode);
        setPrintMode(savedMode);
        setPrintModeSaved(true);
    };

    const handleResetPrintMode = () => {
        const savedMode = savePrintMode(DEFAULT_PRINT_MODE);
        setPrintMode(savedMode);
        setPrintModeSaved(false);
    };

    const fetchTaxes = async () => {
        if (!canReadTaxes) {
            setTaxes([]);
            setTaxesError('No tienes permisos para ver impuestos.');
            return;
        }
        setTaxesLoading(true);
        setTaxesError('');
        try {
            const data = await getTaxes();
            setTaxes(Array.isArray(data) ? data : []);
        } catch (err) {
            setTaxes([]);
            setTaxesError(err.response?.data?.message || 'No se pudieron cargar los impuestos.');
        } finally {
            setTaxesLoading(false);
        }
    };

    const fetchBranchTaxes = async () => {
        if (!canReadTaxes || !branches.length) return;
        setBranchTaxLoading(true);
        setBranchTaxError('');
        try {
            const results = await Promise.all(
                branches.map(async (branch) => ({
                    branchId: branch.id,
                    taxes: await getBranchTaxes(branch.id),
                }))
            );
            const nextMap = {};
            results.forEach((result) => {
                const branchMap = {};
                (result.taxes || []).forEach((tax) => {
                    branchMap[tax.id] = !!tax.branchActive;
                });
                nextMap[result.branchId] = branchMap;
            });
            setBranchTaxMap(nextMap);
        } catch (err) {
            setBranchTaxError(err.response?.data?.message || 'No se pudieron cargar los impuestos por sucursal.');
        } finally {
            setBranchTaxLoading(false);
        }
    };

    const handleCreateTax = async () => {
        if (!canManageTaxes) return;
        const name = String(taxForm.name || '').trim();
        const rate = Number(taxForm.rate);
        if (!name || !Number.isFinite(rate)) {
            setTaxesError('Completa el nombre y una tasa valida.');
            return;
        }
        setTaxSaving(true);
        setTaxesError('');
        try {
            await createTax({
                name,
                rate,
                applyToAllProducts: !!taxForm.applyToAllProducts,
                isActive: !!taxForm.isActive,
                isIncludedInPrice: true,
            });
            setTaxForm({ name: '', rate: '', applyToAllProducts: true, isActive: true });
            await fetchTaxes();
            await fetchBranchTaxes();
        } catch (err) {
            setTaxesError(err.response?.data?.message || 'No se pudo crear el impuesto.');
        } finally {
            setTaxSaving(false);
        }
    };

    const handleTaxEditChange = (taxId, field, value) => {
        setTaxEdits((prev) => ({
            ...prev,
            [taxId]: {
                ...prev[taxId],
                [field]: value,
            },
        }));
    };

    const handleSaveTax = async (taxId) => {
        if (!canManageTaxes) return;
        const draft = taxEdits[taxId];
        if (!draft) return;
        const name = String(draft.name || '').trim();
        const rate = Number(draft.rate);
        if (!name || !Number.isFinite(rate)) {
            setTaxesError('Completa el nombre y una tasa valida.');
            return;
        }
        setTaxSaving(true);
        setTaxesError('');
        try {
            await updateTax(taxId, {
                name,
                rate,
                applyToAllProducts: !!draft.applyToAllProducts,
                isActive: !!draft.isActive,
                isIncludedInPrice: true,
            });
            await fetchTaxes();
            await fetchBranchTaxes();
        } catch (err) {
            setTaxesError(err.response?.data?.message || 'No se pudo actualizar el impuesto.');
        } finally {
            setTaxSaving(false);
        }
    };

    const handleBranchTaxToggle = (branchId, taxId) => {
        setBranchTaxMap((prev) => ({
            ...prev,
            [branchId]: {
                ...(prev[branchId] || {}),
                [taxId]: !(prev[branchId] || {})[taxId],
            },
        }));
        setBranchTaxDirty((prev) => ({ ...prev, [branchId]: true }));
    };

    const handleSaveBranchTaxes = async (branchId) => {
        if (!canManageTaxes) return;
        const branchMap = branchTaxMap[branchId] || {};
        const activeTaxIds = taxes.filter((tax) => branchMap[tax.id]).map((tax) => tax.id);
        setBranchTaxSaving((prev) => ({ ...prev, [branchId]: true }));
        try {
            const result = await updateBranchTaxes(branchId, { activeTaxIds });
            const updated = Array.isArray(result?.taxes) ? result.taxes : [];
            const nextBranchMap = {};
            updated.forEach((tax) => {
                nextBranchMap[tax.id] = !!tax.branchActive;
            });
            setBranchTaxMap((prev) => ({ ...prev, [branchId]: nextBranchMap }));
            setBranchTaxDirty((prev) => ({ ...prev, [branchId]: false }));
        } catch (err) {
            setBranchTaxError(err.response?.data?.message || 'No se pudieron guardar los impuestos.');
        } finally {
            setBranchTaxSaving((prev) => ({ ...prev, [branchId]: false }));
        }
    };

    const handleBranchSelect = (branchId) => {
        const nextId = String(branchId || '');
        if (!nextId || nextId === String(activeBranchId)) return;
        setActiveBranchId(nextId);
        localStorage.setItem('selectedBranchId', nextId);
        navigate({ pathname: '/settings', search: location.search }, { replace: true });
    };

    const handleTopicSelect = (topicId) => {
        const topicConfig = SETTINGS_TOPICS.find((topic) => topic.id === topicId) || SETTINGS_TOPICS[0];
        const nextSection = topicConfig.sections.find((section) => section.id === activeSection)
            ? activeSection
            : topicConfig.sections[0].id;
        setActiveTopic(topicConfig.id);
        setActiveSection(nextSection);
        updateQueryParams(topicConfig.id, nextSection);
    };

    const handleSectionSelect = (sectionId) => {
        setActiveSection(sectionId);
        updateQueryParams(activeTopicConfig.id, sectionId);
    };

    const resetBranchCreateForm = () => {
        setNewBranchName('');
        setNewBranchRegisters([buildRegisterTemplate(0)]);
        setBranchCreateError('');
        setNewBranchTaxSelection({});
    };

    const handleOpenCreateBranch = () => {
        if (!canCreateBranch) return;
        setBranchCreateNotice('');
        resetBranchCreateForm();
        setBranchCreateOpen(true);
    };

    const handleCloseCreateBranch = () => {
        if (branchCreating) return;
        setBranchCreateOpen(false);
        resetBranchCreateForm();
    };

    const updateRegister = (index, updater) => {
        setNewBranchRegisters((prev) =>
            prev.map((register, idx) => (idx === index ? updater(register) : register))
        );
    };

    const handleRegisterNameChange = (index, value) => {
        updateRegister(index, (register) => ({ ...register, name: value }));
    };

    const handleRegisterActiveChange = (index, value) => {
        updateRegister(index, (register) => ({ ...register, isActive: value }));
    };

    const handleRegisterMaxOpenSalesChange = (index, value) => {
        updateRegister(index, (register) => ({ ...register, maxOpenSales: value }));
    };

    const handleRegisterDiscountApproverChange = (index, value) => {
        updateRegister(index, (register) => ({ ...register, discountApproverUserId: value }));
    };

    const toggleRegisterClientType = (index, type) => {
        updateRegister(index, (register) => {
            const current = Array.isArray(register.allowedClientTypes)
                ? register.allowedClientTypes
                : [];
            const next = current.includes(type)
                ? current.filter((entry) => entry !== type)
                : [...current, type];
            return { ...register, allowedClientTypes: next };
        });
    };

    const toggleRegisterUser = (index, userId) => {
        updateRegister(index, (register) => {
            const current = Array.isArray(register.users) ? register.users : [];
            const next = current.includes(userId)
                ? current.filter((entry) => entry !== userId)
                : [...current, userId];
            return { ...register, users: next };
        });
    };

    const handleAddRegister = () => {
        setNewBranchRegisters((prev) => [...prev, buildRegisterTemplate(prev.length)]);
    };

    const handleRemoveRegister = (index) => {
        setNewBranchRegisters((prev) =>
            prev.length > 1 ? prev.filter((_, idx) => idx !== index) : prev
        );
    };

    const handleCreateBranch = async () => {
        if (!canCreateBranch) return;
        if (!canManageRegisters) {
            setBranchCreateError('No tienes permisos para crear cajas.');
            return;
        }
        const trimmedName = newBranchName.trim();
        if (!trimmedName) {
            setBranchCreateError('El nombre de la sucursal es obligatorio.');
            return;
        }
        const registerPayloads = [];
        const seenNames = new Set();
        for (let index = 0; index < newBranchRegisters.length; index += 1) {
            const register = newBranchRegisters[index];
            const trimmedName = register?.name?.trim();
            if (!trimmedName) {
                setBranchCreateError(`El nombre de la caja ${index + 1} es obligatorio.`);
                return;
            }
            const parsedMaxOpenSales = Number.parseInt(register?.maxOpenSales, 10);
            if (!Number.isInteger(parsedMaxOpenSales) || parsedMaxOpenSales < 1) {
                setBranchCreateError(
                    `El limite de ventas abiertas en "${trimmedName}" debe ser un entero >= 1.`
                );
                return;
            }
            const nameKey = trimmedName.toLowerCase();
            if (seenNames.has(nameKey)) {
                continue;
            }
            seenNames.add(nameKey);
            const allowedTypes = Array.isArray(register?.allowedClientTypes)
                ? register.allowedClientTypes.filter((type) => CLIENT_TYPE_LABELS[type])
                : [];
            const selectedUsers = canReadUsers
                ? (Array.isArray(register?.users) ? register.users : [])
                    .map((id) => Number(id))
                    .filter((id) => Number.isInteger(id))
                    .map((userId) => ({ userId, isActive: true }))
                : [];
            const approverId = register?.discountApproverUserId
                ? Number(register.discountApproverUserId)
                : null;
            registerPayloads.push({
                name: trimmedName,
                isActive: register?.isActive ?? true,
                allowedClientTypes: allowedTypes,
                discountApproverUserId: Number.isInteger(approverId) ? approverId : null,
                maxOpenSales: parsedMaxOpenSales,
                users: selectedUsers,
            });
        }
        if (!registerPayloads.length) {
            setBranchCreateError('Agrega al menos una caja.');
            return;
        }
        setBranchCreating(true);
        setBranchCreateError('');
        try {
            const created = await createBranch({ branchName: trimmedName });
            const branchId = created?.id;
            if (!branchId) {
                throw new Error('No se recibio el ID de la sucursal.');
            }
            if (canManageTaxes && Object.keys(newBranchTaxSelection).length) {
                const activeTaxIds = Object.entries(newBranchTaxSelection)
                    .filter(([, isActive]) => isActive)
                    .map(([taxId]) => Number(taxId))
                    .filter((taxId) => Number.isInteger(taxId));
                await updateBranchTaxes(branchId, { activeTaxIds });
            }
            const results = await Promise.allSettled(
                registerPayloads.map((payload) => createCashRegister(branchId, payload))
            );
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            if (successCount === 0) {
                setBranchCreateNotice(
                    'Sucursal creada, pero no se pudo crear ninguna caja. Ve a "Cajas" para crear al menos una.'
                );
            } else if (successCount < registerPayloads.length) {
                setBranchCreateNotice(
                    'Sucursal creada, pero algunas cajas no se pudieron crear.'
                );
            } else {
                setBranchCreateNotice('');
            }
            setBranchCreateOpen(false);
            resetBranchCreateForm();
            fetchBranches();
        } catch (err) {
            setBranchCreateError(
                err.response?.data?.message
                || err.message
                || 'No se pudo crear la sucursal.'
            );
        } finally {
            setBranchCreating(false);
        }
    };

    const handleUpdateBranch = async (branch) => {
        if (!canUpdateBranch) return;
        if (!branch) return;
        const trimmedName = editedBranchName.trim();
        if (!trimmedName) return;
        setBranchError('');
        try {
            await updateBranch(branch.id, { branchName: trimmedName });
            setEditingBranchId(null);
            setEditedBranchName('');
            fetchBranches();
        } catch (err) {
            setBranchError(err.response?.data?.message || 'No se pudo actualizar la sucursal.');
        }
    };

    const handleDeleteBranch = async (branch) => {
        if (!canDeleteBranch) return;
        if (!branch) return;
        const label = branch?.branchName ? ` "${branch.branchName}"` : '';
        const approved = await confirm({
            title: 'Eliminar sucursal',
            message: `Eliminar sucursal${label}?`,
            confirmLabel: 'Eliminar',
            cancelLabel: 'Cancelar',
            tone: 'danger',
        });
        if (!approved) return;
        setBranchError('');
        try {
            await deleteBranch(branch.id);
            if (String(branch.id) === String(activeBranchId)) {
                localStorage.removeItem('selectedBranchId');
                setActiveBranchId('');
            }
            fetchBranches();
        } catch (err) {
            setBranchError(err.response?.data?.message || 'No se pudo eliminar la sucursal.');
        }
    };

    const sourceBranchOptions = branches.filter(
        (branch) => String(branch.id) !== String(activeBranchId)
    );
    const isSyncEnabled = Boolean(priceSync.sourceBranchId);
    const canViewPriceSync = canReadBranches;
    const canEditPriceSync = canUpdateBranch;

    const renderBranchTabs = () => {
        if (!isBranchScopedSection) return null;
        if (branchLoading) {
            return (
                <div className="text-sm text-gray-500">
                    <InlineLoading label="Cargando sucursales" />
                </div>
            );
        }
        if (!activeBranchId && !branches.length) {
            return (
                <div className="text-sm text-gray-500">
                    Selecciona una sucursal para ver esta configuracion.
                </div>
            );
        }
        if (!canReadBranches || !branches.length) {
            return (
                <div className="text-xs text-gray-500">
                    Sucursal actual: <span className="font-medium text-gray-700">{branchLabel}</span>
                </div>
            );
        }
        return (
            <div className="flex gap-2 overflow-x-auto pb-1">
                {branches.map((branch) => {
                    const isActive = String(branch.id) === String(activeBranchId);
                    return (
                        <button
                            key={branch.id}
                            onClick={() => handleBranchSelect(branch.id)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${
                                isActive
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                            }`}
                        >
                            {branch.branchName}
                        </button>
                    );
                })}
            </div>
        );
    };

    const renderUsersAccess = () => {
        if (!canReadUsers) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver usuarios.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="text-xs text-gray-500">
                    Gestiona roles y accesos desde esta vista.
                </div>
                <BranchUsersPage embedded globalView />
            </div>
        );
    };

    const renderBranchDirectory = () => {
        if (!canReadBranches) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver sucursales.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <h2 className="text-md font-semibold">Listado de sucursales</h2>
                        <p className="text-xs text-gray-500">
                            Crea, edita o elimina sucursales desde esta vista.
                        </p>
                    </div>
                    {canCreateBranch && (
                        <div className="flex flex-col md:flex-row md:items-center gap-2">
                            <button
                                type="button"
                                onClick={handleOpenCreateBranch}
                                disabled={!canManageRegisters}
                                className={`bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 ${
                                    !canManageRegisters ? 'opacity-60 cursor-not-allowed' : ''
                                }`}
                            >
                                Nueva sucursal
                            </button>
                            {!canManageRegisters && (
                                <span className="text-xs text-amber-600">
                                    Necesitas permisos de cajas para crear sucursales.
                                </span>
                            )}
                        </div>
                    )}
                </div>
                {branchError && (
                    <div className="text-sm text-red-600">{branchError}</div>
                )}
                {branchCreateNotice && (
                    <div className="text-sm text-amber-600">{branchCreateNotice}</div>
                )}
                <div className="bg-white border rounded-lg divide-y">
                    {branchLoading && (
                        <div className="p-4 text-sm text-gray-500">
                            <InlineLoading label="Cargando sucursales" />
                        </div>
                    )}
                    {!branchLoading && branches.length === 0 && (
                        <div className="p-4 text-sm text-gray-500">
                            No hay sucursales registradas.
                        </div>
                    )}
                    {pagedBranches.map((branch) => (
                        <div key={branch.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div className="flex-1">
                                {editingBranchId === branch.id ? (
                                    <input
                                        type="text"
                                        value={editedBranchName}
                                        onChange={(e) => setEditedBranchName(e.target.value)}
                                        className="w-full px-2 py-1 border rounded"
                                    />
                                ) : (
                                    <div className="text-sm font-medium text-gray-800">{branch.branchName}</div>
                                )}
                                <div className="text-xs text-gray-500">ID {branch.id}</div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {canUpdateBranch && (
                                    editingBranchId === branch.id ? (
                                        <button
                                            onClick={() => handleUpdateBranch(branch)}
                                            className="bg-blue-500 hover:bg-blue-600 px-3 py-1 rounded text-white text-sm"
                                        >
                                            Guardar
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                setEditingBranchId(branch.id);
                                                setEditedBranchName(branch.branchName);
                                            }}
                                            className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-sm"
                                        >
                                            Editar
                                        </button>
                                    )
                                )}
                                {canDeleteBranch && (
                                    <button
                                        onClick={() => handleDeleteBranch(branch)}
                                        className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded text-white text-sm"
                                    >
                                        Eliminar
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {branches.length > branchPageSize && (
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500">
                        <div>
                            Mostrando {branches.length === 0 ? 0 : branchStartIndex + 1}-{branchEndIndex} de {branches.length}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setBranchPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentBranchPage === 1}
                                className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                            >
                                Anterior
                            </button>
                            <span>
                                {currentBranchPage} / {branchTotalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => setBranchPage((prev) => Math.min(prev + 1, branchTotalPages))}
                                disabled={currentBranchPage === branchTotalPages}
                                className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderPriceSync = () => {
        if (!canViewPriceSync) {
            return (
                <div className="border rounded-lg p-4">
                    <h2 className="text-md font-semibold">Casa matriz de precios</h2>
                    <p className="text-xs text-gray-500 mt-1">
                        No tienes permisos para ver la configuracion de precios.
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                <div className="border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div>
                            <h2 className="text-md font-semibold">Casa matriz de precios</h2>
                            <p className="text-xs text-gray-500">
                                Replica cambios de precios desde otra sucursal con ajuste.
                            </p>
                        </div>
                        {canEditPriceSync && (
                            <button
                                onClick={handleSavePriceSync}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-60"
                                disabled={priceSyncLoading}
                            >
                                Guardar configuracion
                            </button>
                        )}
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Casa matriz</span>
                            <select
                                value={priceSync.sourceBranchId}
                                onChange={(e) => handlePriceSyncSourceChange(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={!canEditPriceSync}
                            >
                                <option value="">Sin sincronizacion</option>
                                {sourceBranchOptions.map((branch) => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.branchName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Tipo ajuste</span>
                            <select
                                value={priceSync.adjustmentType}
                                onChange={(e) => handlePriceSyncTypeChange(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={!isSyncEnabled || !canEditPriceSync}
                            >
                                <option value="">Selecciona</option>
                                {Object.keys(PRICE_SYNC_TYPE_LABELS).map((key) => (
                                    <option key={key} value={key}>
                                        {PRICE_SYNC_TYPE_LABELS[key]}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Ajuste</span>
                            <input
                                type="text"
                                value={priceSync.adjustmentValue}
                                onChange={(e) => handlePriceSyncValueChange(e.target.value)}
                                placeholder="0"
                                className="w-40 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={!isSyncEnabled || !canEditPriceSync}
                            />
                            <span className="text-xs text-gray-500">Usa negativo para descuento.</span>
                        </div>
                        <p className="text-xs text-gray-500">
                            El ajuste se aplica a cada cambio de precio. La copia inicial requiere backfill.
                        </p>
                    </div>
                    {!canEditPriceSync && (
                        <p className="text-xs text-gray-500 mt-2">
                            Solo lectura: necesitas permisos para editar.
                        </p>
                    )}
                    {priceSyncError && (
                        <p className="text-sm text-red-600 mt-3">{priceSyncError}</p>
                    )}
                    {priceSyncSaved && (
                        <p className="text-sm text-green-600 mt-3">Configuracion guardada.</p>
                    )}
                </div>

                <div className="border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div>
                            <h2 className="text-md font-semibold">Sucursal principal de solicitudes</h2>
                            <p className="text-xs text-gray-500">
                                Define la sucursal que recibe solicitudes y gestiona envios salientes.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleSaveBranchRequestConfig}
                            className="bg-slate-700 text-white px-3 py-1 rounded text-sm hover:bg-slate-800 disabled:opacity-60"
                            disabled={branchRequestConfigLoading}
                        >
                            Guardar
                        </button>
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <span className="w-28 text-sm font-medium text-gray-700">Principal</span>
                            <select
                                value={branchRequestConfig.mainBranchId}
                                onChange={(e) => handleBranchRequestMainChange(e.target.value)}
                                className="flex-1 p-2 border border-gray-300 rounded disabled:opacity-60"
                                disabled={branchRequestConfigLoading}
                            >
                                <option value="">Automatico (sucursal mas antigua)</option>
                                {branches.map((branch) => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.branchName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <p className="text-xs text-gray-500">
                            Principal efectiva: {
                                branches.find((branch) => String(branch.id) === String(branchRequestConfig.resolvedMainBranchId))?.branchName
                                || 'No definida'
                            }
                        </p>
                    </div>
                    {branchRequestConfigError && (
                        <p className="text-sm text-red-600 mt-3">{branchRequestConfigError}</p>
                    )}
                    {branchRequestConfigSaved && (
                        <p className="text-sm text-green-600 mt-3">Configuracion de solicitudes guardada.</p>
                    )}
                </div>
            </div>
        );
    };

    const renderRegisters = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para gestionar cajas.
                </div>
            );
        }
        if (!canAccessRegisters) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver la configuracion de cajas.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="text-xs text-gray-500">
                    Administra cajas, usuarios y limites desde esta vista.
                </div>
                <CashRegistersPage branchId={activeBranchId} embedded />
            </div>
        );
    };

    const renderCatalogProducts = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para gestionar productos.
                </div>
            );
        }
        if (!canReadProducts) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver productos.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="text-xs text-gray-500">
                    Gestiona productos, precios y variedades desde esta vista.
                </div>
                <ProductList
                    branchId={activeBranchId}
                    embedded
                    canReadProducts={canReadProducts}
                    canCreateProduct={canCreateProduct}
                    canUpdateProduct={canUpdateProduct}
                    canDeleteProduct={canDeleteProduct}
                />
            </div>
        );
    };

    const renderCatalogCategories = () => {
        if (!activeBranchId) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    Selecciona una sucursal para gestionar categorias.
                </div>
            );
        }
        if (!canReadCategories) {
            return (
                <div className="border rounded-lg p-4 text-sm text-gray-600">
                    No tienes permisos para ver categorias.
                </div>
            );
        }
        return (
            <div className="space-y-4">
                <div className="text-xs text-gray-500">
                    Configura categorias y disponibilidad por sucursal.
                </div>
                <CategoriesAdminPage branchId={activeBranchId} embedded />
            </div>
        );
    };

    const renderBarcodes = () => (
        <div className="border rounded-lg p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                    <h2 className="text-md font-semibold">Codigos de barras</h2>
                    <p className="text-xs text-gray-500">
                        Prefijos de 1 digito para codigos de 13 digitos.
                    </p>
                </div>
                <button
                    onClick={handleAddMapping}
                    className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                >
                    + Prefijo
                </button>
            </div>

            <div className="space-y-3">
                {mappings.map((mapping, index) => (
                    <div
                        key={`${mapping.prefix}-${index}`}
                        className="flex items-center gap-3"
                    >
                        <input
                            type="text"
                            value={mapping.prefix}
                            placeholder="Prefijo"
                            inputMode="numeric"
                            maxLength={BARCODE_PREFIX_MAX_LENGTH}
                            pattern="[0-9]*"
                            onChange={(e) => handlePrefixChange(index, e.target.value)}
                            className="w-20 p-2 border border-gray-300 rounded"
                        />
                        <select
                            value={mapping.mode}
                            onChange={(e) => handleModeChange(index, e.target.value)}
                            className="flex-1 p-2 border border-gray-300 rounded"
                        >
                            {BARCODE_MODE_OPTIONS.map((mode) => (
                                <option key={mode} value={mode}>
                                    {MODE_LABELS[mode] || mode}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => handleRemoveMapping(index)}
                            className="text-red-600 text-sm hover:text-red-700"
                        >
                            Quitar
                        </button>
                    </div>
                ))}
            </div>

            <div className="mt-6 border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                <div>
                    <h3 className="text-sm font-semibold">Regla global de selección automática</h3>
                    <p className="text-xs text-gray-500">
                        Define si la selección se basa en cantidad acumulada por variedad o subtotal del documento.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="text-xs text-gray-700">
                        Modo
                        <select
                            className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                            value={barcodePricingRule.mode}
                            onChange={(e) => handleBarcodeRuleModeChange(e.target.value)}
                            disabled={barcodeRuleLoading}
                        >
                            <option value="quantity">Cantidad por variedad</option>
                            <option value="subtotal">Subtotal documento</option>
                        </select>
                    </label>
                    <div className="text-xs text-gray-500 flex items-end">
                        Si más de un tipo califica, se aplica el precio calificado más barato.
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.entries(CLIENT_TYPE_LABELS).map(([type, label]) => (
                        <label key={type} className="text-xs text-gray-700">
                            Umbral {label}
                            <input
                                type="text"
                                inputMode="numeric"
                                className="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
                                value={barcodePricingRule.thresholds[type] ?? ''}
                                onChange={(e) => handleBarcodeRuleThresholdChange(type, e.target.value)}
                                placeholder="Sin configurar"
                                disabled={barcodeRuleLoading}
                            />
                        </label>
                    ))}
                </div>
                {barcodeRuleError && (
                    <p className="text-xs text-red-600">{barcodeRuleError}</p>
                )}
                {barcodeRuleSaved && !barcodeRuleError && (
                    <p className="text-xs text-green-600">Regla global guardada.</p>
                )}
                <div>
                    <button
                        type="button"
                        onClick={handleSaveBarcodeRule}
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-60"
                        disabled={barcodeRuleLoading}
                    >
                        {barcodeRuleLoading ? 'Guardando...' : 'Guardar regla'}
                    </button>
                </div>
            </div>

            {error && (
                <p className="text-sm text-red-600 mt-3">{error}</p>
            )}
            {saved && (
                <p className="text-sm text-green-600 mt-3">Configuracion guardada.</p>
            )}

            <div className="flex items-center gap-3 mt-4">
                <button
                    onClick={handleSave}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                >
                    Guardar
                </button>
                <button
                    onClick={handleReset}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300"
                >
                    Restablecer
                </button>
            </div>
        </div>
    );

    const renderPrintSettings = () => (
        <div className="border rounded-lg p-4 space-y-4">
            <div>
                <h2 className="text-md font-semibold">Impresion de comprobantes</h2>
                <p className="text-xs text-gray-500">
                    Define como imprimir desde este navegador.
                </p>
            </div>
            <div className="space-y-3">
                {PRINT_MODE_OPTIONS.map((mode) => (
                    <label
                        key={mode}
                        className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${
                            printMode === mode
                                ? 'border-blue-200 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        <input
                            type="radio"
                            name="print-mode"
                            value={mode}
                            checked={printMode === mode}
                            onChange={(e) => handlePrintModeChange(e.target.value)}
                            className="mt-1"
                        />
                        <div>
                            <div className="text-sm font-medium">
                                {PRINT_MODE_LABELS[mode] || mode}
                            </div>
                            {PRINT_MODE_DESCRIPTIONS[mode] && (
                                <div className="text-xs text-gray-500">
                                    {PRINT_MODE_DESCRIPTIONS[mode]}
                                </div>
                            )}
                        </div>
                    </label>
                ))}
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={handleSavePrintMode}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                >
                    Guardar
                </button>
                <button
                    onClick={handleResetPrintMode}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300"
                >
                    Restablecer
                </button>
            </div>
            {printModeSaved && (
                <p className="text-sm text-green-600">Configuracion guardada.</p>
            )}
            <p className="text-xs text-gray-500">
                Este ajuste se guarda en el navegador actual.
            </p>
        </div>
    );

    const renderTaxSettings = () => (
        <div className="space-y-6">
            <div className="border rounded-lg p-4 space-y-4">
                <div>
                    <h2 className="text-md font-semibold">Impuestos</h2>
                    <p className="text-xs text-gray-500">Administra el IVA y otros impuestos.</p>
                </div>
                {!canReadTaxes && (
                    <p className="text-sm text-gray-500">No tienes permisos para ver impuestos.</p>
                )}
                {canReadTaxes && taxesError && (
                    <p className="text-sm text-red-600">{taxesError}</p>
                )}
                {canReadTaxes && taxesLoading && (
                    <InlineLoading label="Cargando impuestos" />
                )}

                {canReadTaxes && !taxesLoading && (
                    <>
                        {canManageTaxes && (
                            <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                                <div className="text-sm font-semibold text-gray-700">Crear impuesto</div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                        <input
                                            type="text"
                                            value={taxForm.name}
                                            onChange={(e) => setTaxForm((prev) => ({ ...prev, name: e.target.value }))}
                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                            disabled={taxSaving}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 uppercase">Tasa (%)</label>
                                        <input
                                            type="number"
                                            value={taxForm.rate}
                                            onChange={(e) => setTaxForm((prev) => ({ ...prev, rate: e.target.value }))}
                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                            disabled={taxSaving}
                                        />
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={taxForm.applyToAllProducts}
                                                onChange={(e) => setTaxForm((prev) => ({ ...prev, applyToAllProducts: e.target.checked }))}
                                                disabled={taxSaving}
                                            />
                                            Aplicar a todos
                                        </label>
                                        <label className="flex items-center gap-2 text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={taxForm.isActive}
                                                onChange={(e) => setTaxForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                                                disabled={taxSaving}
                                            />
                                            Activo
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                        onClick={handleCreateTax}
                                        disabled={taxSaving}
                                    >
                                        {taxSaving ? 'Guardando...' : 'Agregar impuesto'}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                            <div className="text-sm font-semibold text-gray-700">Impuestos registrados</div>
                            {taxes.length === 0 && (
                                <p className="text-xs text-gray-500">No hay impuestos registrados.</p>
                            )}
                            {taxes.map((tax) => {
                                const draft = taxEdits[tax.id] || {};
                                return (
                                    <div key={tax.id} className="border border-gray-100 rounded-lg p-3 space-y-2">
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                                            <div className="md:col-span-2">
                                                <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                                <input
                                                    type="text"
                                                    value={draft.name ?? tax.name}
                                                    onChange={(e) => handleTaxEditChange(tax.id, 'name', e.target.value)}
                                                    className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                    disabled={!canManageTaxes || taxSaving}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-semibold text-gray-600 uppercase">Tasa (%)</label>
                                                <input
                                                    type="number"
                                                    value={draft.rate ?? tax.rate}
                                                    onChange={(e) => handleTaxEditChange(tax.id, 'rate', e.target.value)}
                                                    className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                    disabled={!canManageTaxes || taxSaving}
                                                />
                                            </div>
                                            <div className="flex flex-wrap gap-3">
                                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={draft.applyToAllProducts ?? tax.applyToAllProducts}
                                                        onChange={(e) => handleTaxEditChange(tax.id, 'applyToAllProducts', e.target.checked)}
                                                        disabled={!canManageTaxes || taxSaving}
                                                    />
                                                    Aplicar a todos
                                                </label>
                                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                                    <input
                                                        type="checkbox"
                                                        checked={draft.isActive ?? tax.isActive}
                                                        onChange={(e) => handleTaxEditChange(tax.id, 'isActive', e.target.checked)}
                                                        disabled={!canManageTaxes || taxSaving}
                                                    />
                                                    Activo
                                                </label>
                                            </div>
                                        </div>
                                        {canManageTaxes && (
                                            <div>
                                                <button
                                                    type="button"
                                                    className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                                                    onClick={() => handleSaveTax(tax.id)}
                                                    disabled={taxSaving}
                                                >
                                                    Guardar
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            <div className="border rounded-lg p-4 space-y-4">
                <div>
                    <h2 className="text-md font-semibold">Activacion por sucursal</h2>
                    <p className="text-xs text-gray-500">
                        Define que impuestos aplican en cada sucursal.
                    </p>
                </div>
                {!canReadTaxes && (
                    <p className="text-sm text-gray-500">No tienes permisos para ver impuestos por sucursal.</p>
                )}
                {canReadTaxes && branchTaxError && (
                    <p className="text-sm text-red-600">{branchTaxError}</p>
                )}
                {canReadTaxes && branchTaxLoading && (
                    <InlineLoading label="Cargando impuestos por sucursal" />
                )}
                {canReadTaxes && !branchTaxLoading && taxes.length === 0 && (
                    <p className="text-xs text-gray-500">No hay impuestos registrados.</p>
                )}
                {canReadTaxes && !branchTaxLoading && taxes.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Sucursal</th>
                                    {taxes.map((tax) => (
                                        <th
                                            key={tax.id}
                                            className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase"
                                        >
                                            {tax.name}
                                        </th>
                                    ))}
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {branches.map((branch) => {
                                    const branchMap = branchTaxMap[branch.id] || {};
                                    const isSaving = !!branchTaxSaving[branch.id];
                                    const isDirty = !!branchTaxDirty[branch.id];
                                    return (
                                        <tr key={branch.id}>
                                            <td className="px-3 py-2 font-medium text-gray-800">
                                                {branch.branchName || `Sucursal ${branch.id}`}
                                            </td>
                                            {taxes.map((tax) => (
                                                <td key={tax.id} className="px-3 py-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!branchMap[tax.id]}
                                                        onChange={() => handleBranchTaxToggle(branch.id, tax.id)}
                                                        disabled={!canManageTaxes || tax.isActive === false || isSaving}
                                                    />
                                                </td>
                                            ))}
                                            <td className="px-3 py-2 text-right">
                                                {canManageTaxes && (
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-60"
                                                        onClick={() => handleSaveBranchTaxes(branch.id)}
                                                        disabled={!isDirty || isSaving}
                                                    >
                                                        {isSaving ? 'Guardando...' : 'Guardar'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {branches.length === 0 && (
                                    <tr>
                                        <td colSpan={taxes.length + 2} className="px-3 py-4 text-center text-xs text-gray-500">
                                            No hay sucursales registradas.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    const renderSectionContent = () => {
        switch (activeSectionConfig?.id) {
            case 'users-access':
                return renderUsersAccess();
            case 'branch-directory':
                return renderBranchDirectory();
            case 'branch-price-sync':
                return renderPriceSync();
            case 'registers-cash':
                return renderRegisters();
            case 'registers-barcode':
                return renderBarcodes();
            case 'registers-printing':
                return renderPrintSettings();
            case 'taxes-manage':
                return renderTaxSettings();
            case 'catalog-products':
                return renderCatalogProducts();
            case 'catalog-categories':
                return renderCatalogCategories();
            case 'catalog-price-list':
                return (
                    <AdminPriceListPage
                        embedded
                        onOpenFullPage={() =>
                            navigate(
                                activeBranchId
                                    ? `/branches/${activeBranchId}/price-list`
                                    : '/branches'
                            )
                        }
                    />
                );
            default:
                return null;
        }
    };

    const showSidebarHeader = !isSidebarCollapsed;
    const sidebarHeaderClass = isSidebarCollapsed ? 'hidden md:group-hover:block' : 'block';
    const sidebarLabelClass = isSidebarCollapsed
        ? 'md:hidden md:group-hover:block'
        : 'block';
    const sidebarDescriptionClass = isSidebarCollapsed
        ? 'hidden md:group-hover:block'
        : 'hidden md:block';
    const sidebarSectionClass = isSidebarCollapsed
        ? 'hidden md:group-hover:block'
        : 'block';

    return (
        <div className="min-h-[90vh] bg-gray-100 pt-16 px-3 pb-6">
            <div className="max-w-6xl mx-auto">
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="border-b px-5 py-3">
                        <h1 className="text-xl font-bold">Configuracion</h1>
                        <p className="text-sm text-gray-600">
                            Administra usuarios, sucursales y cajas desde una sola vista.
                        </p>
                    </div>
                    <div className="flex flex-col md:flex-row">
                        <aside
                            className={`group w-full md:flex-none border-b md:border-b-0 md:border-r p-3 bg-white overflow-hidden transition-[width,max-height] ${
                                isSidebarCollapsed ? 'max-h-28 md:w-16 md:hover:w-72' : 'max-h-[720px] md:w-72'
                            } md:max-h-none`}
                        >
                            <div
                                className={`flex items-center mb-3 ${
                                    showSidebarHeader ? 'justify-between' : 'justify-center'
                                }`}
                            >
                                <div className={`text-xs uppercase text-gray-400 ${sidebarHeaderClass}`}>
                                    Configuracion
                                </div>
                                <div className="flex items-center gap-2">
                                    {!isSidebarCollapsed && (
                                        <button
                                            type="button"
                                            onClick={() => setIsSidebarCollapsed((prev) => !prev)}
                                            className="md:hidden text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1"
                                            aria-label="Ocultar opciones"
                                        >
                                            <FiChevronUp />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setIsSidebarCollapsed((prev) => !prev)}
                                        className="hidden md:inline-flex text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1"
                                        aria-label={isSidebarCollapsed ? 'Expandir' : 'Contraer'}
                                    >
                                        <TiPin className={isSidebarCollapsed ? '-rotate-45' : ''} />
                                    </button>
                                </div>
                            </div>
                            <div className="relative">
                                <div className="flex flex-col gap-2 overflow-y-auto pr-1">
                                    {SETTINGS_TOPICS.map((topic) => {
                                        const isActive = topic.id === activeTopicConfig.id;
                                        return (
                                            <div key={topic.id} className="flex flex-col">
                                                <button
                                                    onClick={() => handleTopicSelect(topic.id)}
                                                    className={`w-full text-left px-3 py-2 rounded-lg border ${
                                                        isActive
                                                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                                                            : 'border-transparent hover:border-gray-200 hover:bg-gray-50 text-gray-600'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-base">{topicIcons[topic.id] || <FiSettings />}</span>
                                                        <div className={sidebarLabelClass}>
                                                            <div className="text-sm font-semibold">{topic.label}</div>
                                                            <div className={`text-xs text-gray-500 ${sidebarDescriptionClass}`}>
                                                                {topic.description}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </button>
                                                <div
                                                    className={`mt-1 ml-6 rounded-lg border border-gray-200 bg-gray-100 p-1.5 flex flex-col gap-1 ${
                                                        isActive ? sidebarSectionClass : 'hidden'
                                                    }`}
                                                >
                                                    {topic.sections.map((section) => {
                                                        const isSectionActive = section.id === activeSectionConfig?.id;
                                                        return (
                                                            <button
                                                                key={section.id}
                                                                onClick={() => handleSectionSelect(section.id)}
                                                                className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${
                                                                    isSectionActive
                                                                        ? 'border-blue-200 bg-white text-blue-700'
                                                                        : 'border-transparent hover:border-gray-200 text-gray-600'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-sm">
                                                                        {sectionIcons[section.id] || <FiSettings />}
                                                                    </span>
                                                                    <div className={sidebarLabelClass}>
                                                                        <div className="font-medium">{section.label}</div>
                                                                        <div className={`text-xs text-gray-500 ${sidebarDescriptionClass}`}>
                                                                            {section.description}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {isSidebarCollapsed && (
                                    <button
                                        type="button"
                                        onClick={() => setIsSidebarCollapsed((prev) => !prev)}
                                        className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 px-2 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm"
                                        aria-label="Mostrar opciones"
                                    >
                                        <FiChevronDown />
                                    </button>
                                )}
                            </div>
                        </aside>
                        <div className="p-4 flex-1">
                            <div className="flex-1 space-y-6">
                                {renderBranchTabs()}
                                {renderSectionContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {branchCreateOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-[1000] flex justify-center items-start overflow-y-auto px-4 py-6"
                    onClick={handleCloseCreateBranch}
                >
                    <div
                        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500">Sucursales</p>
                                <h2 className="text-lg font-semibold text-gray-900">Nueva sucursal</h2>
                                <p className="text-xs text-gray-500 mt-1">
                                    Crea la sucursal y configura al menos una caja.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleCloseCreateBranch}
                                className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
                                disabled={branchCreating}
                            >
                                x
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            <div>
                                <label className="text-xs font-semibold text-gray-600 uppercase">
                                    Nombre de sucursal
                                </label>
                                <input
                                    type="text"
                                    value={newBranchName}
                                    onChange={(e) => setNewBranchName(e.target.value)}
                                    className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                    placeholder="Sucursal principal"
                                    disabled={branchCreating}
                                />
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-semibold text-gray-600 uppercase">
                                        Cajas iniciales
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleAddRegister}
                                        className="text-xs text-blue-600 hover:text-blue-700"
                                        disabled={branchCreating}
                                    >
                                        + Agregar caja
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {newBranchRegisters.map((register, index) => {
                                        const registerLabel = register?.name?.trim() || `Caja ${index + 1}`;
                                        const allowedTypes = Array.isArray(register?.allowedClientTypes)
                                            ? register.allowedClientTypes
                                            : [];
                                        const selectedUsers = Array.isArray(register?.users) ? register.users : [];
                                        const showUsersGrid =
                                            canReadUsers
                                            && !branchCreateUsersLoading
                                            && !branchCreateUsersError
                                            && branchCreateAvailableUsers.length > 0;
                                        return (
                                            <div key={`register-${index}`} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="text-sm font-semibold text-gray-800">{registerLabel}</h4>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveRegister(index)}
                                                        disabled={branchCreating || newBranchRegisters.length === 1}
                                                        className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                                    >
                                                        Quitar
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                                                        <input
                                                            type="text"
                                                            value={register?.name || ''}
                                                            onChange={(e) => handleRegisterNameChange(index, e.target.value)}
                                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                            placeholder={`Caja ${index + 1}`}
                                                            disabled={branchCreating}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-600 uppercase">Limite de ventas abiertas</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={register?.maxOpenSales ?? '5'}
                                                            onChange={(e) => handleRegisterMaxOpenSalesChange(index, e.target.value)}
                                                            className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                                                            disabled={branchCreating}
                                                        />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <label className="inline-flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={register?.isActive ?? true}
                                                                onChange={(e) => handleRegisterActiveChange(index, e.target.checked)}
                                                                disabled={branchCreating}
                                                            />
                                                            Caja activa
                                                        </label>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block">
                                                        Tipos de cliente permitidos
                                                    </label>
                                                    <div className="flex flex-wrap gap-3">
                                                        {Object.entries(CLIENT_TYPE_LABELS).map(([type, label]) => (
                                                            <label key={type} className="flex items-center gap-2 text-sm text-gray-700">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={allowedTypes.includes(type)}
                                                                    onChange={() => toggleRegisterClientType(index, type)}
                                                                    disabled={branchCreating}
                                                                />
                                                                {label}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block">
                                                        Encargado de autorizacion de descuentos
                                                    </label>
                                                    <select
                                                        className="w-full border border-gray-300 rounded-lg p-2"
                                                        value={register?.discountApproverUserId || ''}
                                                        onChange={(e) => handleRegisterDiscountApproverChange(index, e.target.value)}
                                                        disabled={branchCreating || !canReadUsers || branchCreateUsersLoading || Boolean(branchCreateUsersError)}
                                                    >
                                                        <option value="">Sin encargado</option>
                                                        {branchCreateAvailableUsers.map((user) => (
                                                            <option key={user.id} value={user.id}>
                                                                {user.name || user.username}
                                                                {hasPermission(user, 'branch.access.any') ? ' (Admin)' : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {!canReadUsers && (
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            No tienes permisos para cargar usuarios.
                                                        </p>
                                                    )}
                                                    {canReadUsers && branchCreateUsersLoading && (
                                                        <div className="mt-2">
                                                            <InlineLoading label="Cargando usuarios" />
                                                        </div>
                                                    )}
                                                    {canReadUsers && !branchCreateUsersLoading && branchCreateUsersError && (
                                                        <p className="text-xs text-red-600 mt-1">{branchCreateUsersError}</p>
                                                    )}
                                                    {canReadUsers
                                                        && !branchCreateUsersLoading
                                                        && !branchCreateUsersError
                                                        && branchCreateAvailableUsers.length === 0 && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                No hay usuarios administradores disponibles.
                                                            </p>
                                                        )}
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block">
                                                        Usuarios con acceso
                                                    </label>
                                                    {!canReadUsers && (
                                                        <p className="text-xs text-gray-500">
                                                            No tienes permisos para cargar usuarios.
                                                        </p>
                                                    )}
                                                    {canReadUsers && branchCreateUsersLoading && (
                                                        <InlineLoading label="Cargando usuarios" />
                                                    )}
                                                    {canReadUsers && !branchCreateUsersLoading && branchCreateUsersError && (
                                                        <p className="text-xs text-red-600">{branchCreateUsersError}</p>
                                                    )}
                                                    {showUsersGrid && (
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                                                            {branchCreateAvailableUsers.map((user) => (
                                                                <label key={user.id} className="flex items-center gap-2 text-sm text-gray-700">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedUsers.includes(String(user.id))}
                                                                        onChange={() => toggleRegisterUser(index, String(user.id))}
                                                                        disabled={branchCreating}
                                                                    />
                                                                    <span>{user.name || user.username}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {canReadUsers
                                                        && !branchCreateUsersLoading
                                                        && !branchCreateUsersError
                                                        && branchCreateAvailableUsers.length === 0 && (
                                                            <p className="text-xs text-gray-500">
                                                                Asigna usuarios desde "Usuarios" o crea administradores.
                                                            </p>
                                                        )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    Se requiere al menos una caja.
                                </p>
                            </div>

                            <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm space-y-3">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-800">Impuestos para la sucursal</h4>
                                    <p className="text-xs text-gray-500">
                                        Selecciona los impuestos que estaran activos en esta sucursal.
                                    </p>
                                </div>
                                {!canReadTaxes && (
                                    <p className="text-xs text-gray-500">No tienes permisos para ver impuestos.</p>
                                )}
                                {canReadTaxes && taxesLoading && (
                                    <InlineLoading label="Cargando impuestos" />
                                )}
                                {canReadTaxes && !taxesLoading && taxesError && (
                                    <p className="text-xs text-red-600">{taxesError}</p>
                                )}
                                {canReadTaxes && !taxesLoading && !taxesError && taxes.length === 0 && (
                                    <p className="text-xs text-gray-500">No hay impuestos registrados.</p>
                                )}
                                {canReadTaxes && !taxesLoading && taxes.length > 0 && (
                                    <div className="flex flex-wrap gap-3">
                                        {taxes.map((tax) => (
                                            <label
                                                key={tax.id}
                                                className={`flex items-center gap-2 text-sm ${
                                                    tax.isActive === false ? 'text-gray-400' : 'text-gray-700'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!newBranchTaxSelection[tax.id]}
                                                    onChange={(e) =>
                                                        setNewBranchTaxSelection((prev) => ({
                                                            ...prev,
                                                            [tax.id]: e.target.checked,
                                                        }))
                                                    }
                                                    disabled={branchCreating || tax.isActive === false}
                                                />
                                                {tax.name} ({Number(tax.rate || 0)}%)
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {branchCreateError && (
                                <p className="text-sm text-red-600">{branchCreateError}</p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50">
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:text-gray-900"
                                onClick={handleCloseCreateBranch}
                                disabled={branchCreating}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                onClick={handleCreateBranch}
                                disabled={branchCreating}
                            >
                                {branchCreating ? 'Creando...' : 'Crear sucursal'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsPage;
